import { ReactComponent as ArrowDownIcon } from '@elementary/common-ui/assets/16x16/arrow-down.svg';
import { ActionChip, Menu, SvgIcon } from '@elementary/common-ui/components';
import { CircularProgress } from '@mui/material';
import { ReactComponent as NewTabIcon } from '@elementary/common-ui/assets/20x20/new-tab.svg';
import {
  extractTicketIdFromUrl,
  ticketingIconMap,
} from 'shared/components/ticketing/utils';
import useIncidentTicketing from '../../hooks/useIncidentTicketing';
import { IncidentOverviewData } from '../../types';
import IncidentTicketCreateDialog from './IncidentTicketCreateDialog';

type Props = {
  incident: IncidentOverviewData;
};

const IncidentTicketChip = ({ incident }: Props) => {
  const {
    selectedEnvId,
    enabledIntegrations,
    ticketDialogOpen,
    selectedIntegration,
    onCreateTicketClick,
    handleDialogConfirm,
    handleDialogClose,
    isCreatingTicket,
    dialogError,
    handleViewTicket,
    incidentUrl,
    isResolved,
    hasTickets,
    hasSingleIntegration,
    hasMultipleIntegrations,
  } = useIncidentTicketing({
    sourceType: incident.sourceType,
    sourceId: incident.sourceId,
    incidentId: incident.id,
    status: incident.status,
    ticketsCount: incident.tickets?.length,
  });

  if (!selectedEnvId) {
    return null;
  }

  return (
    <>
      {incident.tickets?.map((ticket) => {
        const serviceLower = ticket.service.toLowerCase().replace('_', '');
        return (
          <ActionChip
            key={ticket.serviceTicketId}
            icon={ticketingIconMap[serviceLower]}
            label={
              extractTicketIdFromUrl(
                ticket.serviceTicketUrl,
                serviceLower,
                ticket.serviceTicketId,
              ) || 'View ticket'
            }
            onClick={() => handleViewTicket(ticket.serviceTicketUrl)}
            endIcon={
              <SvgIcon height={16} width={16}>
                <NewTabIcon />
              </SvgIcon>
            }
          />
        );
      })}

      {!hasTickets && hasSingleIntegration && !isResolved && (
        <ActionChip
          icon={
            isCreatingTicket ? (
              <CircularProgress size={14} />
            ) : (
              ticketingIconMap[enabledIntegrations![0].type]
            )
          }
          label="Create ticket"
          onClick={() => onCreateTicketClick(enabledIntegrations![0].id)}
        />
      )}

      {!hasTickets && hasMultipleIntegrations && !isResolved && (
        <Menu
          closeOnChange
          menuButton={
            <ActionChip
              icon={
                isCreatingTicket ? (
                  <CircularProgress size={14} />
                ) : (
                  <ArrowDownIcon />
                )
              }
              label="Create ticket"
            />
          }
          options={enabledIntegrations?.map((integration) => ({
            value: integration.id,
            label: integration.name,
            icon: ticketingIconMap[integration.type],
          }))}
          onChange={(value) => onCreateTicketClick(value)}
        />
      )}

      {ticketDialogOpen && selectedIntegration && selectedEnvId && (
        <IncidentTicketCreateDialog
          open={ticketDialogOpen}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
          incidentId={incident.id}
          integration={selectedIntegration}
          envId={selectedEnvId}
          incidentUrl={incidentUrl}
          isCreating={isCreatingTicket}
          error={dialogError}
        />
      )}
    </>
  );
};

export default IncidentTicketChip;
